import { type SetStoreState, createStore } from '@/stores/createStore';

export type AgentBuilderState = {
  /**
   * ID of the agent builder currently loaded in the editor, or undefined if
   * no editor session is active. Cross-feature consumers (AiAssistant,
   * Header.atom) read this and look the data up via useAgentBuilderQuery.
   * The graph itself lives in the React Query cache.
   */
  currentAgentBuilderId: string | undefined;
  /** ID of the node selected in the inspector panel, or undefined if none. */
  selectedNodeId: string | undefined;
  /** True when the editor has unsaved local changes vs. the loaded server state. */
  isDirty: boolean;
  /** ID of the version the user is viewing/editing, or null if no version is selected. */
  selectedVersionId: string | null;
  /** ID of the version the current draft was forked from, or null if not a draft. */
  draftBaseVersionId: string | null;
  /** Map of canonical node type -> tool names exposed to the LLM by that node. */
  capabilitiesByType: Record<string, string[]>;
  /** True after the capabilities map has been fetched at least once for this session. */
  capabilitiesLoaded: boolean;
};

const initialState: AgentBuilderState = {
  currentAgentBuilderId: undefined,
  selectedNodeId: undefined,
  isDirty: false,
  selectedVersionId: null,
  draftBaseVersionId: null,
  capabilitiesByType: {},
  capabilitiesLoaded: false,
};

const agentBuilderActionsFactory = (set: SetStoreState<AgentBuilderState>) => ({
  /**
   * Set the active agent builder id pointer and isDirty in one atomic update.
   * The dirty flag tracks meta.hasDraft from the loaded graph; the page
   * computes hasDraft from the React Query cache and passes it here.
   */
  setCurrentAgentBuilderId: (id: string | undefined, hasDraft = false) =>
    set((draft) => {
      draft.currentAgentBuilderId = id;
      draft.isDirty = hasDraft;
    }),

  setSelectedNode: (nodeId: string | undefined) =>
    set((draft) => {
      draft.selectedNodeId = nodeId;
    }),

  markAgentBuilderClean: () =>
    set((draft) => {
      draft.isDirty = false;
    }),

  markAgentBuilderDirty: () =>
    set((draft) => {
      draft.isDirty = true;
    }),

  setAgentBuilderSelectedVersion: (versionId: string | null) =>
    set((draft) => {
      draft.selectedVersionId = versionId;
    }),

  setAgentBuilderDraftBaseVersion: (versionId: string | null) =>
    set((draft) => {
      draft.draftBaseVersionId = versionId;
    }),

  /**
   * Cache the per-node-type tool names map fetched from
   * /agent-builder/node-capabilities. The data is process-static on the
   * backend, so once `capabilitiesLoaded` flips to true we don't refetch.
   */
  setNodeCapabilities: (capabilitiesByType: Record<string, string[]>) =>
    set((draft) => {
      draft.capabilitiesByType = capabilitiesByType;
      draft.capabilitiesLoaded = true;
    }),

  /** Full state wipe to initial values. Fired on AgentBuilder.page unmount. */
  resetAgentBuilder: () => set(() => structuredClone(initialState)),
});

const [AgentBuilderStoreProvider, useAgentBuilderStoreInstance, useAgentBuilderSelector] = createStore(
  'AgentBuilder',
  initialState,
  agentBuilderActionsFactory,
);

export { AgentBuilderStoreProvider };

export const useCurrentAgentBuilderId = (enabled = true): string | undefined =>
  useAgentBuilderSelector((s) => (enabled ? s.currentAgentBuilderId : undefined));
export const useSelectedNodeId = () => useAgentBuilderSelector((s) => s.selectedNodeId);
export const useIsDirty = (enabled = true): boolean => useAgentBuilderSelector((s) => (enabled ? s.isDirty : false));
export const useSelectedVersionId = (enabled = true): string | null =>
  useAgentBuilderSelector((s) => (enabled ? s.selectedVersionId : null));
export const useDraftBaseVersionId = (enabled = true): string | null =>
  useAgentBuilderSelector((s) => (enabled ? s.draftBaseVersionId : null));
export const useCapabilitiesByType = () => useAgentBuilderSelector((s) => s.capabilitiesByType);
export const useCapabilitiesLoaded = () => useAgentBuilderSelector((s) => s.capabilitiesLoaded);

/** Stable actions handle — never causes re-renders. */
export const useAgentBuilderActions = () => useAgentBuilderStoreInstance().actions;
